import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { DiscoveryProfile } from "@/types";

// #226: Discovery-Mutationen (CV setzen, Flags/Scope patchen, neu indizieren).
// Alle invalidieren ["resources","agents"] → Agent-Detail (AgentResponse.discovery)
// + die discovery-Sub-Resource refetchen atomisch.

function useDiscoveryClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

export interface DiscoverySettingsPatch {
  discoverable?: boolean;
  allowRegister?: boolean;
  allowConnectionRequest?: boolean;
  scope?: string;
}

export function useSetDiscoveryProfile(): UseMutationResult<
  DiscoveryProfile,
  Error,
  { agentId: string; cvText: string }
> {
  const qc = useQueryClient();
  const getClient = useDiscoveryClient();
  return useMutation({
    mutationFn: ({ agentId, cvText }) =>
      getClient().post<DiscoveryProfile>(`/agents/${agentId}/discovery/profile`, { cvText }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("agents") }),
  });
}

export function useSetDiscoverySettings(): UseMutationResult<
  DiscoveryProfile,
  Error,
  { agentId: string; patch: DiscoverySettingsPatch }
> {
  const qc = useQueryClient();
  const getClient = useDiscoveryClient();
  return useMutation({
    mutationFn: ({ agentId, patch }) =>
      getClient().patch<DiscoveryProfile>(`/agents/${agentId}/discovery/settings`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("agents") }),
  });
}

export function useReindexDiscovery(): UseMutationResult<DiscoveryProfile, Error, string> {
  const qc = useQueryClient();
  const getClient = useDiscoveryClient();
  return useMutation({
    mutationFn: (agentId) => getClient().post<DiscoveryProfile>(`/agents/${agentId}/discovery/reindex`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("agents") }),
  });
}
